/* global VXConfig */
import React, { useState, useEffect, useRef, useCallback }   from 'react';
import { Route, useNavigate, useLocation, Navigate, Routes, useParams } from 'react-router-dom';

import ActorProfileHeader           from './ActorHeader/ActorProfileHeader.jsx';
import ActorProfileFeed             from './ActorProfileFeed/ActorProfileFeed.js';
import ActorProfileOverview         from './ActorProfileOverview/ActorProfileOverview';
import ActorProfileStickyHeader     from './ActorHeader/ActorProfileStickyHeader.jsx';
import ActorProfileTopCams          from './ActorProfileTopCams/ActorProfileTopCams.jsx';
import ActorProfileVideoDetail      from './ActorProfileVideos/ActorProfileVideoDetail';
import ActorProfileVideoOverview    from './ActorProfileVideos/ActorProfileVideoOverview';
import Breadcrumb                   from '../Breadcrumb';
import LogoLoader                   from '../Loader/LogoLoader.js';
import ProfileAboutMe               from "./ActorProfileAbout/ProfileAboutMe.js";
import ProfileLiveShow              from '../Profile/ProfileLiveShow/ProfileLiveShow.js';

import Flux          from '../../flux/Flux';
import ProfileRoutes from './ProfileRoutes.js';
import Translations  from '../../utils/Translations';
import UrlBuilder    from '../../utils/UrlBuilder';
import { VXButtonConfig } from '../SimpleElements/VXButton/VXButtonConfig.js';
import { isObjectEmpty }  from '../../utils/CommonUtils.js';

import './../../../../src/VX/Asset/Less/VX/View/Actor/ActorProfile.less';

const actorDataPlaceholder = {

    isPlaceholder:            true,

    age:                      0,
    coverPictureBig:          '',
    headerPicture:            '',
    id:                       0,
    isDildocontrolAvailable:  false,
    isOnline:                 false,
    isOnlineMobileVideocall:  false,
    isServiceNumberAvailable: false,
    isVoyeurChatAvailable:    false,
    name:                     '',
    previewPicture:           '',
    profileCoverPhotoUrl:     '',
    profileLink:              '',
    welcomeclip:              null,
};

const ActorProfile = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const params   = useParams();

    const lastActorName        = useRef(null);
    const breadcrumbRef        = useRef(null);
    const navigationRef        = useRef(null);
    const prevActiveRouteRef   = useRef(null);
    const hasPassedThreshold   = useRef(false);
    const lastNotification     = useRef(null);

    const [actorData, setActorData]               = useState({});
    const [routes, setRoutes]                     = useState({});
    const [sedcard, setSedcard]                   = useState({});
    const [showStickyHeader, setShowStickyHeader] = useState(false);
    const [videoRouteInfo, setVideoRouteInfo]     = useState({});

    const { actorName } = params;

    const isGuestLoggedIn = Flux.Guest.isLoggedIn();
    const lineBreakPoint  = 1200;

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const scrollToBreadcrumbs = () => {
        const offset         = window.innerWidth < lineBreakPoint ? 50 : -15;
        const offsetPosition = breadcrumbRef.current.offsetTop - offset;
        window.scrollTo({
            top:      offsetPosition,
            behavior: 'smooth',
        });
    };

    const navigateTo = useCallback((path, e, shouldScrollToBreadcrumbs = true) => {

        // open in new tab if ctrl (or cmd) key is pressed or middle mouse button is clicked
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            const url = new URL(path.toLowerCase(), window.location.origin);
            window.open(url.href, "_blank");
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        navigate(path.toLowerCase());

        shouldScrollToBreadcrumbs && scrollToBreadcrumbs();
    }, []);

    const setPromotedVideo = (updatedPromotedVideo) => {
        setActorData({
            ...actorData,
            promotedVideo: updatedPromotedVideo,
        });
    }

    const getActiveRoute = () => {

        if(videoRouteInfo && location.pathname === videoRouteInfo.path) {
            return ({
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_VIDEO_DETAILS,
                path:     videoRouteInfo.path,
                classic:  videoRouteInfo.classic,
                albumId:  videoRouteInfo.id,
            })
        }

        return Object.values(routes).find(route => route.isActive);
    };

    const getCurrentActiveKey = (routes) => {
        let activeKey      = null;
        let maxMatchLength = 0;

        for (const key in routes) {
            if (routes.hasOwnProperty(key)) {
                const route       = routes[key];
                const matchLength = location.pathname.includes(route.path) ? route.path.length : 0;

                if (matchLength > maxMatchLength) {
                    maxMatchLength = matchLength;
                    activeKey = key;
                }
            }
        }

        // to difference if video detail is showing a classic video or a normal video
        if((activeKey === 'videos') && videoRouteInfo?.classic && (location.pathname === videoRouteInfo?.path)) {
            activeKey = 'classicVideos';
        }
        return activeKey;
    }

    const getRoutesWithCurrentActiveRoute = (routes) => {
        const activeKey = getCurrentActiveKey(routes);
        return Object.fromEntries(
            Object.entries(routes).map(([key, route]) => [
                key,
                {
                    ...route,
                    isActive: key === activeKey,
                },
            ])
        );
    };

    const getRoutes = (data) => {
        const isVideoRouteActive = (location.pathname?.includes(UrlBuilder.buildActorProfileVideosUrl(data.name))
            && !location.pathname?.includes(UrlBuilder.buildActorVideoClassicUrl(data.name))
        );
        const navRoutes = {
            overview: {
                index:    1,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_OVERVIEW,
                path:     UrlBuilder.buildActorProfileUrl(data.name),
                label:    Translations.get(data?.isFeedAvailable
                            ? 'actor_profile.feed.nav_field'
                            : 'actor_profile.overview.nav_field'
                ),
                isActive: location.pathname === UrlBuilder.buildActorProfileUrl(data.name),
            },
            aboutMe: {
                index:    2,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_ABOUT,
                path:     UrlBuilder.buildActorProfileAboutMeUrl(data.name),
                label:    Translations.get('actor_profile.about_me.nav_field'),
                isActive: location.pathname === UrlBuilder.buildActorProfileAboutMeUrl(data.name),
            },
            videos: {
                index:    3,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_VIDEOS_OVERVIEW,
                path:     UrlBuilder.buildActorProfileVideosUrl(data.name),
                label:    Translations.get('actor_profile.videos.nav_field'),
                count:    data.videosCount,
                isActive: isVideoRouteActive,
            },
        };

        if(data.hasClassicContent && (data.classicVideosCount > 0)) {
            navRoutes.classicVideos = {
                index:    4,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_VIDEOS_CLASSIC,
                path:     UrlBuilder.buildActorVideoClassicUrl(data.name),
                label:    Translations.get('actor_profile.classic_videos.nav_field'),
                count:    data.classicVideosCount,
                isActive: window.location.pathname?.includes(UrlBuilder.buildActorVideoClassicUrl(data.name)),
                classic:  true,
            }
        }

        if(data.topCamsTitle) {
            navRoutes.topCams = {
                index:    6,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_TOP_CAMGIRLS,
                path:     UrlBuilder.buildActorProfileTopCamsUrl(data.name),
                label:    data.topCamsTitle,
                isActive: window.location.pathname === UrlBuilder.buildActorProfileTopCamsUrl(data.name),
                isNew:    false,
            };
        }

        if(data.hasUpcomingTicketShow) {
            navRoutes.liveShow = {
                index:    7,
                key:      Flux.Constants.Breadcrumb.ACTOR_PROFILE_LIVE_SHOW,
                path:     UrlBuilder.buildActorProfileLiveShowUrl(data.name),
                label:    Translations.get('LiveShow'),
                isActive: window.location.pathname === UrlBuilder.buildActorProfileLiveShowUrl(data.name),
                isNew:    false,
            };
        }
        return navRoutes;
    };

    const handleWindowScroll = () => {
        if(navigationRef?.current) {
            const rect       = navigationRef.current.getBoundingClientRect();
            const isNavAtTop = rect.top <= 45;
            if(isNavAtTop !== hasPassedThreshold.current) {
                hasPassedThreshold.current = isNavAtTop;
                setShowStickyHeader(isNavAtTop);
            }
        }
    };

    useEffect(() => {
        // to avoid unnecessary re-renders
        if(actorName === lastActorName.current) return;
        lastActorName.current = actorName;

        const loadOrGetActorData = () => {
            const data = Flux.Actor.getActorDataByName(actorName);
            if (!isObjectEmpty(data)) {
                setActorData(data);

                const navRoutes = getRoutes(data);
                setRoutes(navRoutes);
            } else {
                Flux.Actor.loadActorDataByName(actorName);
            }
            scrollToTop();
        };

        loadOrGetActorData();

        const onActorDataChange = () => {
            loadOrGetActorData();
        };

        const onSedcardPinStatusChanged = (actorId, status) => {
            const album = Flux.Gallery.getSedcards(actorId);
            if(Object.keys(album).length > 0) {
                setSedcard({
                    ...album,
                    isFavoriteAlbum: status,
                });
            }
        };

        const onAddActorToFavorites = () => {
            setActorData(prevData => ({
                ...prevData,
                isFavorite: true,
            }));
        };
        const onRemoveActorFromFavorites = () => {
            setActorData(prevData => ({
                ...prevData,
                isFavorite: false,
            }));
        };

        window.addEventListener('scroll', handleWindowScroll);

        Flux.Actor.addActorDataChangeListener(onActorDataChange);
        Flux.Favorite.addSedcardsAlbumPinStatusListener(onSedcardPinStatusChanged);
        Flux.Favorite.addActorPinnedListener(onAddActorToFavorites);
        Flux.Favorite.addActorUnpinnedListener(onRemoveActorFromFavorites);

        return () => {
            window.removeEventListener('scroll', handleWindowScroll);

            Flux.Actor.removeActorDataChangeListener(onActorDataChange);
            Flux.Favorite.removeSedcardsAlbumPinStatusListener(onSedcardPinStatusChanged);
            Flux.Favorite.removeActorPinnedListener(onAddActorToFavorites);
            Flux.Favorite.removeActorUnpinnedListener(onRemoveActorFromFavorites);
        }
    }, [actorName]);

    useEffect(() => {
        const onActorOnlineStateChange = (notification) => {
            // Avoid unnecessary re-renders
            if(notification.actorId !== actorData.id) return;
            if(lastNotification.current === JSON.stringify(notification)) return;
            lastNotification.current = JSON.stringify(notification);
            setActorData(prevData => {
                if(isObjectEmpty(prevData)) return prevData;

                const NotificationTypes = Flux.Constants.ApiTypes.Notification.Incoming;
                const onlineStatus      = {
                    isOnline:                prevData.isOnline,
                    isVoyeurChatAvailable:   prevData.isVoyeurChatAvailable,
                    isLobbyChat:             prevData.isLobbyChat,
                    isCurrentlyTicketShow:   prevData.isCurrentlyTicketShow,
                    isOnlineMobileVideocall: prevData.isOnlineMobileVideocall,
                    isService0900Busy:       prevData.isService0900Busy,
                };

                switch (notification.type) {
                    case NotificationTypes.ACTOR_ONLINE:
                        let isCurrentlyTicketShow = prevData.isCurrentlyTicketShow;

                        if (prevData.isLobbyChat && !notification.isLobbyChat) {
                            isCurrentlyTicketShow = false;
                        }
                        onlineStatus.isOnline                = true;
                        onlineStatus.isVoyeurChatAvailable   = notification.canVoyeurChat;
                        onlineStatus.isLobbyChat             = notification.isLobbyChat;
                        onlineStatus.isCurrentlyTicketShow   = isCurrentlyTicketShow;
                        onlineStatus.isOnlineMobileVideocall = false;

                        break;
                    case NotificationTypes.ACTOR_OFFLINE:
                        onlineStatus.isOnline                = false;
                        onlineStatus.isLobbyChat             = false;
                        onlineStatus.isCurrentlyTicketShow   = false;
                        onlineStatus.isOnlineMobileVideocall = notification.isOnlineMobileVideocall;
                        onlineStatus.singleC2CChatPrice      = notification.singleC2CChatPrice;

                        break;
                    case NotificationTypes.ACTOR_SETTINGS:
                        onlineStatus.isLivePreviewEnabled = notification.isLivePreviewEnabled;

                        break;
                    case NotificationTypes.FREE_SHOW_START:
                        onlineStatus.freeShowData = notification.data;
                        break;
                    case NotificationTypes.FREE_SHOW_STOP:
                        onlineStatus.freeShowData = null;

                        break;
                    case NotificationTypes.VOICECALL_START :
                        if (Flux.Guest.isLoggedIn()) {
                            onlineStatus.isVoicecallActive = true;
                        }
                        break;
                    case NotificationTypes.VOICECALL_STOP :
                        if (Flux.Guest.isLoggedIn()) {
                            onlineStatus.isVoicecallActive = false;
                        }
                        break;
                    case NotificationTypes.SERVICE0900_STATUS_CHANGED :
                        onlineStatus.isService0900Busy = notification.isBusy;
                        break;
                    default:
                        break;
                }

                return {
                    ...prevData,
                    ...onlineStatus,
                };
            });
        };

        Flux.Notification.addNotificationChangeListener(onActorOnlineStateChange);
        return () => {
            Flux.Notification.removeNotificationChangeListener(onActorOnlineStateChange);
        };
    }, [actorData]);

    useEffect(()=>{
        setRoutes((prevRoutes) => {
            if(isObjectEmpty(prevRoutes)) return prevRoutes;
            const activeRoutes = getRoutesWithCurrentActiveRoute(prevRoutes);
            return activeRoutes;
        });
        // for animation in Navigation Item Arrow (::after)
        prevActiveRouteRef.current = getActiveRoute();
    }, [location, videoRouteInfo]);

    const redirectToOverview = () => <Navigate to={routes.overview?.path} replace />;

    const copyActorIdToClipboard = (e) => {
        const target = e.target;
        navigator.clipboard.writeText(target.innerText);
        target.style.cursor = "none"
        setTimeout(() => {
            target.style.cursor = "pointer"
        }, 100);
    };

    return (

        <>
            <ActorProfileHeader
                actorData            = {!isObjectEmpty(actorData) ? actorData : actorDataPlaceholder}
                isGuestLoggedIn      = {isGuestLoggedIn}
                lastActiveRouteIndex = {prevActiveRouteRef.current?.index}
                lineBreakPoint       = {lineBreakPoint}
                navigateTo           = {navigateTo}
                navigationRef        = {navigationRef}
                routes               = {routes}
                scrollToBreadcrumbs  = {scrollToBreadcrumbs}
            />

            {!(isObjectEmpty(actorData) || isObjectEmpty(routes)) ? <>
                {showStickyHeader && <ActorProfileStickyHeader
                    actorData       = {actorData}
                    isGuestLoggedIn = {isGuestLoggedIn}
                    navigationRef   = {navigationRef}
                />}

                {getActiveRoute() &&
                    <Breadcrumb
                        profilePath    = {routes?.overview?.path}
                        navigateTo     = {navigateTo}
                        breadcrumbType = {getActiveRoute().key}
                        breadcrumbKey  = {getActiveRoute().key + '-' + (actorData.id ?? 0)}
                        isMobile       = {false}
                        breadcrumbData = {{
                            actorId: actorData.id ?? 0,
                            albumId: getActiveRoute().albumId,
                            classic: getActiveRoute().classic
                        }}
                        breadcrumbRef = {breadcrumbRef}
                    />
                }

                <div className='actor-profile-body__container'>
                    <p className="actor-profile-id-stealth-mode" onClick={(e)=>copyActorIdToClipboard(e)}>{actorData.id}</p>
                    <Routes>
                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_OVERVIEW)}
                            element = {actorData.isFeedAvailable ?
                                <ActorProfileFeed
                                    actorId   = {actorData.id}
                                    actorData = {actorData}
                                    isMobile  = {VXButtonConfig.isVXMobilePlatform}
                                /> :
                                <ActorProfileOverview
                                    actorData       = {actorData}
                                    isGuestLoggedIn = {isGuestLoggedIn}
                                    routes          = {routes}
                                    navigateTo      = {navigateTo}
                                />
                            }
                        />
                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_ABOUT_ME)}
                            element = {
                                <ProfileAboutMe
                                    actorData  = {actorData}
                                    navigateTo = {navigateTo}
                                    sedcard    = {sedcard}
                                    setSedcard = {setSedcard}
                                />
                            }
                        />
                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_VIDEOS)}
                            element = {
                                <ActorProfileVideoOverview
                                    actorData        = {actorData}
                                    navigateTo       = {navigateTo}
                                    location         = {location}
                                    setPromotedVideo = {setPromotedVideo}
                                />
                            }
                        />

                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_VIDEOS_TAG_GROUP)}
                            element = {
                                <ActorProfileVideoOverview
                                    actorData        = {actorData}
                                    navigateTo       = {navigateTo}
                                    setPromotedVideo = {setPromotedVideo}
                                />
                            }
                        />

                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_CLASSIC_VIDEOS)}
                            element = { actorData.hasClassicContent
                                ? <ActorProfileVideoOverview
                                    actorData  = {actorData}
                                    navigateTo = {navigateTo}
                                    isClassics = {true}
                                /> : <Navigate to = {routes.videos.path} replace />
                            }
                        />

                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_VIDEO_DETAILS)}
                            element = {
                                <ActorProfileVideoDetail
                                    actorData         = {actorData}
                                    location          = {location}
                                    navigateTo        = {navigateTo}
                                    setVideoRouteInfo = {setVideoRouteInfo}
                                />
                            }
                        />


                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_PHOTOS)}
                            element = {redirectToOverview()}
                        />
                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_TOP_CAMS)}
                            element = {actorData.topCamsTitle
                                ? <ActorProfileTopCams
                                    actorId={actorData.id}
                                /> : redirectToOverview()
                            }
                        />
                        <Route
                            path    = {ProfileRoutes.getPathByName(ProfileRoutes.ACTOR_LIVESHOW)}
                            element = {actorData.hasUpcomingTicketShow
                                ? <ProfileLiveShow
                                    actorId               = {actorData.id}
                                    hasUpcomingTicketShow = {actorData.hasUpcomingTicketShow}
                                    previewPicture        = {actorData.previewPicture}
                                /> : redirectToOverview()
                            }
                        />
                    </Routes>
                </div>
            </> : <LogoLoader pulse={true} isRelative={true}/> }
    </>);
};

export default ActorProfile;
